import React, { useEffect, useState } from "react";
import "./EditBlog.css";
import DOMPurify from "dompurify";
import { GetBlog, UpdateYourBlog } from "../../api/blogApis/blogApi";
import imageCompression from "browser-image-compression";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { blogPostStatus } from "../../app/Enums/StatusEnums";
import { routePath } from "../../app/routes/routepath";
import Feedback from "../../app/components/FeedbackModal/Feedback";
import CdnEditor from "../../app/components/CkEditorCustom/CdnEditor";
import TechnologySelector from "../../app/components/TechnologySelector/TechnologySelector";

export default function EditBlog() {
  const [authorId, setAuthorId] = useState();
  const [roleId, setroleId] = useState();

  const navigate = useNavigate();
  const location = useLocation();
  const SelectedblogId = location.state?.blogId;
  const [isProcessing, setisProcessing] = useState(null);

  const [submitResponse, setSubmitResponse] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const [finalData, setFinalData] = useState({
    authorId: null,
    roleId: null,
    blogId: null,
    blogTitle: "",
    blogContent: "",
    blogTechnologies: [],
    blogDescription: "",
    blogCoverImage: null,
    actionType: 0,
  });

  useEffect(() => {
    if (SelectedblogId) {
      fetchBlogById(SelectedblogId);
    }
    if (localStorage.getItem("token") != null) {
      const id = parseInt(localStorage.getItem("UserId"));
      const role = parseInt(localStorage.getItem("RoleId"));
      setAuthorId(id);
      setroleId(role);
      setFinalData((prev) => ({ ...prev, authorId: id, roleId: role }));
    } else {
      toast.error("Please login to post a blog.");
      navigate(routePath.dashboard);
    }
  }, []);

  useEffect(() => {
    setFinalData((prev) => ({
      ...prev,
      blogTechnologies: selectedOptions,
    }));
  }, [selectedOptions]);

  const fetchBlogById = async (id) => {
    try {
      const response = await GetBlog({ blogId: id });
      if (response.result === 1) {
        const decodedTitle = decodeHtml(response.result_value.blog_title);
        const blogContent = response.result_value.blog_content;
        const stringIds = decodeHtml(response.result_value.blog_technologyIds);
        const selectedTechIds = stringIds
          .split(",")
          .map(Number)
          .filter((n) => !isNaN(n));
        const decodedDescription = decodeHtml(
          response.result_value.blog_description,
        );
        const blogCoverImage = response.result_value.blog_coverImage;

        setFinalData((prev) => ({
          ...prev,
          blogId: id,
          blogTitle: decodedTitle,
          blogContent: blogContent,
          blogTechnologies: [],
          blogDescription: decodedDescription,
          blogCoverImage: blogCoverImage,
          actionType: 0,
        }));

        setSelectedOptions(selectedTechIds);
      } else {
        toast.error("Something went wrong while fetching the blog.");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("API Error: Failed to fetch blog data.");
    }
  };

  const decodeHtml = (encodedStr) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = encodedStr;
    return txt.value;
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setFinalData((prev) => ({ ...prev, blogTitle: value }));
  };

  const handleContentChange = (value) => {
    setFinalData((prev) => ({ ...prev, blogContent: value }));
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setFinalData((prev) => ({ ...prev, blogDescription: value }));
  };

  const handleCoverImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
        const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Image size cannot exceed 20 MB");
      event.target.value = ""; // optional: clear input
      return;
    }
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(file, options);
      const base64 = await convertToBase64(compressedFile);
      setFinalData((prev) => ({ ...prev, blogCoverImage: base64 }));
    } catch (error) {
      console.error("Compression or conversion error:", error);
      toast.error("Image compression failed.");
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const sanitizeInputs = () => {
    const cleanHTML = DOMPurify.sanitize(finalData.blogContent, {
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
      FORBID_TAGS: ["iframe", "object", "embed"],
    });
    return {
      ...finalData,
      blogTitle: DOMPurify.sanitize(finalData.blogTitle),
      blogDescription: DOMPurify.sanitize(finalData.blogDescription),
      blogContent: cleanHTML,
    };
  };

  const handleBlogSubmission = async () => {
    if (!validateForm()) return;
    const updatedData = {
      ...sanitizeInputs(),
      actionType: blogPostStatus.submit,
    };
    setisProcessing("Publish Blog");
    try {
      const response = await UpdateYourBlog(updatedData);
      if (response.result === 1) {
        setSubmitResponse(true);
        setFeedback({
          open: true,
          type: "success",
          title: "Success",
          message: "Your blog has been updated successfully.",
        });
      } else {
        setSubmitResponse(false);
        setFeedback({
          open: true,
          type: "error",
          title: "Failed",
          message: "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error("Submission Error:", error);
      setSubmitResponse(false);
      setFeedback({
        open: true,
        type: "error",
        title: "Failed",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setisProcessing(null);
    }
  };

  const handleDraftedBlogSubmission = async () => {
    if (!validateForm()) return;
    const updatedData = {
      ...sanitizeInputs(),
      actionType: blogPostStatus.draft,
    };
    setisProcessing("Save as Draft");
    try {
      const response = await UpdateYourBlog(updatedData);
      if (response.result === 1) {
        setSubmitResponse(true);
        setFeedback({
          open: true,
          type: "success",
          title: "Success",
          message: "Your blog has been saved to draft!",
        });
      } else {
        setSubmitResponse(false);
        setFeedback({
          open: true,
          type: "error",
          title: "Failed",
          message: "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error("Draft Error:", error);
      setSubmitResponse(false);
      setFeedback({
        open: true,
        type: "error",
        title: "Failed",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setisProcessing(null);
    }
  };

  const validateForm = () => {
    if (
      !finalData.blogTitle.trim() ||
      !finalData.blogContent.trim() ||
      selectedOptions.length === 0 ||
      !finalData.blogDescription.trim() ||
      !finalData.blogCoverImage
    ) {
      toast.error("Please fill all the required fields.");
      return false;
    }
    return true;
  };

  const handleBack = () => {
    navigate(routePath.adminBlogs);
  };

  return (
    <>
      <div className="role-box">
        <ul className="breadcumb">
          <li className="active" onClick={handleBack}>
            <i className="bi bi-file-earmark-richtext"></i>
            Blog
          </li>
          <li className="dash-arrow">/</li>
          <li>
            <span>Edit Blog</span>
          </li>
        </ul>
      </div>

      <div>
        <div className="main_widget">
          <div className="w-100 border-radius-6">
            <div className="form-header">
              <h2>Edit Blog</h2>
            </div>
            <div className="p-20">
              {/* Title */}
              <div className="blog_form_Wraper">
                <label>
                  Title<sup>*</sup>
                </label>
                <div className="input_box">
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      placeholder="Enter blog title (max 80 characters)"
                      value={finalData.blogTitle}
                      className="form-control"
                      maxLength={80}
                      onChange={handleTitleChange}
                      style={{ paddingRight: "55px" }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "12px",
                        color:
                          finalData.blogTitle.length >= 80 ? "red" : "#999",
                        pointerEvents: "none",
                      }}
                    >
                      {finalData.blogTitle.length}/80
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="blog_form_Wraper">
                <label>
                  Content<sup>*</sup>
                </label>
                <div className="input_box">
                  <CdnEditor
                    value={finalData.blogContent}
                    onChange={handleContentChange}
                  />
                </div>
              </div>

              {/* Tags / Technology Selector */}
              <div className="blog_form_Wraper">
                <label>
                  Tags<sup>*</sup>
                </label>
                <TechnologySelector
                  selectedIds={selectedOptions}
                  onChange={setSelectedOptions}
                  maxSelections={5}
                />
              </div>

              {/* Summary */}
              <div className="blog_form_Wraper">
                <label>
                  Summary<sup>*</sup>
                </label>
                <div className="input_box">
                  <input
                    type="text"
                    placeholder="Keywords"
                    className="form-control"
                    value={finalData.blogDescription}
                    onChange={handleDescriptionChange}
                  />
                </div>
              </div>

              {/* Cover Image */}
              <div className="blog_form_Wraper">
                <label>
                  Upload Cover Image<sup>*</sup>
                </label>
                <div className="upload-container">
                  <h2>Upload Your File</h2>
                  <label htmlFor="file-upload" className="custom-file-upload">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                      <path
                        d="M12 16v-8M8 12l4-4 4 4M4 20h16"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Choose File
                  </label>
                   <p class="limit-text">Maximum upload size 20 MB</p>
                  <input
                    id="file-upload"
                    type="file"
                    onChange={handleCoverImageChange}
                  />
                  <div>
                    {finalData.blogCoverImage && (
                      <img
                        src={finalData.blogCoverImage}
                        style={{ width: 300, height: 200, borderRadius: 10 }}
                        alt="Blog Cover"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="blog_form_btn_wraper">
                <button
                  className="delete_approve_btn"
                  onClick={handleDraftedBlogSubmission}
                  disabled={isProcessing === "Save as Draft"}
                >
                  {isProcessing === "Save as Draft" ? (
                    <div className="draft_spinner"></div>
                  ) : (
                    "Save as Draft"
                  )}
                </button>
                <button
                  className="delete_reject_btn"
                  onClick={handleBlogSubmission}
                  disabled={isProcessing === "Publish Blog"}
                >
                  {isProcessing === "Publish Blog" ? (
                    <div className="Publish_spinner"></div>
                  ) : (
                    "Publish Blog"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Feedback
        isOpen={feedback.open}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        onClose={() => {
          setFeedback({ ...feedback, open: false });
          navigate(routePath.adminBlogs);
        }}
      />
    </>
  );
}
